import { useState } from 'react';
import { Play } from 'lucide-react';
import { Badge } from '@medikei/ui';
import { Button } from '@/components/ui/Button';
import { Pagination, PaginationInfo } from '@/components/ui/pagination';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import type { PayoutBatch, PayoutBatchStatus } from '../types/admin-partner';

interface PayoutBatchListProps {
  batches: PayoutBatch[];
  isLoading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onProcess: (batchId: string) => void;
  isProcessing?: boolean;
}

const statusVariants: Record<PayoutBatchStatus, 'warning' | 'primary' | 'success' | 'danger'> = {
  PENDING: 'warning',
  PROCESSING: 'primary',
  COMPLETED: 'success',
  FAILED: 'danger',
};

const statusLabels: Record<PayoutBatchStatus, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  COMPLETED: 'Concluído',
  FAILED: 'Falhou',
};

export function PayoutBatchList({
  batches,
  isLoading,
  total,
  page,
  pageSize,
  onPageChange,
  onProcess,
  isProcessing,
}: PayoutBatchListProps) {
  const [selectedBatch, setSelectedBatch] = useState<PayoutBatch | null>(null);

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  if (isLoading) {
    return <LoadingOverlay message="Carregando lotes..." />;
  }

  if (batches.length === 0) {
    return (
      <EmptyState
        title="Nenhum lote de pagamento encontrado"
        description="Não há lotes de pagamento cadastrados."
        action={{
          label: 'Gerar Novo Lote',
          onClick: () => {},
        }}
      />
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">ID</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Valor Total</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Quantidade</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Gerado em</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Processado em</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {batches.map((batch) => (
              <tr key={batch.id} className="hover:bg-muted/30">
                <td className="px-4 py-3 text-sm font-mono">{batch.id.slice(0, 8)}...</td>
                <td className="px-4 py-3 text-sm font-medium">
                  {formatCurrency(Number(batch.amount || 0))}
                </td>
                <td className="px-4 py-3 text-sm">{batch.count ?? (batch as any).payoutCount ?? 0}</td>
                <td className="px-4 py-3">
                  <Badge variant={statusVariants[batch.status]}>
                    {statusLabels[batch.status]}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {formatDate(batch.generatedAt)}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {batch.processedAt ? formatDate(batch.processedAt) : '-'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedBatch(batch)}
                    >
                      Ver Detalhes
                    </Button>
                    {batch.status === 'PENDING' && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onProcess(batch.id)}
                        disabled={isProcessing}
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Processar
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <PaginationInfo
          currentPage={page}
          pageSize={pageSize}
          totalItems={total}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>

      <Dialog open={!!selectedBatch} onOpenChange={() => setSelectedBatch(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Lote #{selectedBatch?.id.slice(0, 8)}</DialogTitle>
            <DialogDescription>
              Informações detalhadas dos pagamentos deste lote.
            </DialogDescription>
          </DialogHeader>
          {selectedBatch && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                  <p className="text-xl font-bold">{formatCurrency(Number(selectedBatch.amount || 0))}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Quantidade</p>
                  <p className="text-xl font-bold">{selectedBatch.count ?? (selectedBatch as any).payoutCount ?? 0}</p>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium">Parceiro</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Valor</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(selectedBatch.payouts || []).map((payout) => (
                      <tr key={payout.id}>
                        <td className="px-4 py-2 text-sm">{payout.partnerName}</td>
                        <td className="px-4 py-2 text-sm">{formatCurrency(payout.amount)}</td>
                        <td className="px-4 py-2">
                          <Badge
                            variant={
                              payout.status === 'COMPLETED'
                                ? 'success'
                                : payout.status === 'FAILED'
                                ? 'danger'
                                : 'warning'
                            }
                          >
                            {payout.status === 'COMPLETED'
                              ? 'Concluído'
                              : payout.status === 'FAILED'
                              ? 'Falhou'
                              : 'Pendente'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedBatch.errorMessage && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm font-medium text-destructive">Erro:</p>
                  <p className="text-sm text-destructive">{selectedBatch.errorMessage}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedBatch(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
